import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FormEvent, PropsWithChildren } from "react";
import { useState } from "react";

import { api } from "../../shared/api/client";
import type { Teacher } from "../../shared/api/types";
import { image } from "../../shared/assets/manifest";
import { Button } from "../../shared/ui/Button";
import { Field } from "../../shared/ui/Field";
import { LoadingBlock } from "../../shared/ui/LoadingBlock";

type AuthMe = {
  setupRequired: boolean;
  setupTokenRequired: boolean;
  teacher: Teacher | null;
};

export function AuthGate({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const auth = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => api<AuthMe>("/api/auth/me"),
    select: (payload) => ({
      setupRequired: Boolean(payload.setupRequired),
      setupTokenRequired: Boolean(payload.setupTokenRequired),
      teacher: payload.teacher || null
    })
  });

  const login = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      api("/api/auth/login", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: async () => {
      setMessage("");
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
      await queryClient.invalidateQueries({ queryKey: ["state"] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "登录失败")
  });

  const setup = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      api("/api/auth/setup", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: async () => {
      setMessage("");
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
      await queryClient.invalidateQueries({ queryKey: ["state"] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "初始化失败")
  });

  if (auth.isLoading) {
    return (
      <main className="auth-page">
        <LoadingBlock />
      </main>
    );
  }

  if (auth.data?.teacher) return <>{children}</>;

  const setupRequired = Boolean(auth.data?.setupRequired);
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(Array.from(form.entries()).map(([key, value]) => [key, String(value)]));
    if (setupRequired) {
      if (payload.password !== payload.confirmPassword) {
        setMessage("两次输入的密码不一致");
        return;
      }
      delete payload.confirmPassword;
      setup.mutate(payload);
    } else {
      login.mutate(payload);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-hero">
        <div className="auth-title-row">
          <img src={image.star} alt="" />
          <div>
            <h1>班级积分管理系统</h1>
            <p>给每一次努力都贴上一颗小星星</p>
          </div>
        </div>
        <ol className="auth-steps" aria-label="使用流程">
          <li className="auth-step"><strong>1</strong><span>创建老师账号</span></li>
          <li className="auth-step"><strong>2</strong><span>进入星星班级</span></li>
          <li className="auth-step"><strong>3</strong><span>开始鼓励同学</span></li>
        </ol>
        <img className="auth-scene" src={image.garden} alt="" />
      </section>
      <section className="auth-panel">
        <img
          className="auth-panel-mascot"
          src={image.dockBear}
          alt=""
          aria-hidden="true"
        />
        <h2>{setupRequired ? "初始化管理员" : "老师登录"}</h2>
        <form className="form-stack" onSubmit={onSubmit}>
          {setupRequired ? (
            <>
              <Field label="教师姓名">
                <input name="name" maxLength={30} required />
              </Field>
              <Field label="登录账号">
                <input name="username" minLength={3} maxLength={40} autoComplete="username" required />
              </Field>
              <Field label="密码">
                <input name="password" type="password" minLength={8} autoComplete="new-password" required />
              </Field>
              <Field label="确认密码">
                <input name="confirmPassword" type="password" minLength={8} autoComplete="new-password" required />
              </Field>
              {auth.data?.setupTokenRequired ? (
                <Field label="初始化口令">
                  <input name="setupToken" type="password" autoComplete="off" required />
                </Field>
              ) : null}
            </>
          ) : (
            <>
              <Field label="登录账号">
                <input name="username" autoComplete="username" required />
              </Field>
              <Field label="密码">
                <input name="password" type="password" autoComplete="current-password" required />
              </Field>
            </>
          )}
          {message ? <div className="inline-error">{message}</div> : null}
          <Button variant="primary" type="submit" disabled={login.isPending || setup.isPending}>
            {setupRequired ? "创建账号" : "登录"}
          </Button>
        </form>
      </section>
    </main>
  );
}
